import { ref, onMounted, onUnmounted, computed } from 'vue'
import { syncManager, type SyncResult } from '../utils/syncManager'
import { offlineStorage } from '../utils/offlineStorage'

/**
 * Composable for managing offline state and synchronization
 */
export function useOffline() {
  const isOnline = ref(navigator.onLine)
  const isSyncing = ref(false)
  const pendingActionsCount = ref(0)
  const lastSyncResult = ref<SyncResult | null>(null)

  const hasOfflineChanges = computed(() => pendingActionsCount.value > 0)

  // Update online status
  const updateOnlineStatus = () => {
    isOnline.value = navigator.onLine
  }

  // Update pending actions count
  const updatePendingCount = async () => {
    pendingActionsCount.value = await syncManager.getPendingCount()
  }

  // Handle sync completion
  const handleSyncComplete = (result: SyncResult) => {
    lastSyncResult.value = result
    isSyncing.value = false
    updatePendingCount()
  }

  // Manual sync trigger
  const triggerSync = async (): Promise<SyncResult> => {
    if (!isOnline.value || isSyncing.value) {
      return { success: false, synced: 0, failed: 0, errors: [] }
    }
    isSyncing.value = true
    return syncManager.syncPendingActions()
  }

  // Clear all offline data
  const clearOfflineData = async () => {
    await offlineStorage.clearAll()
    await updatePendingCount()
  }

  let unsubscribe: (() => void) | null = null

  onMounted(() => {
    // Listen to online/offline events
    window.addEventListener('online', updateOnlineStatus)
    window.addEventListener('offline', updateOnlineStatus)

    // Listen to sync completion
    unsubscribe = syncManager.onSyncComplete(handleSyncComplete)

    // Initial count
    updatePendingCount()
  })

  onUnmounted(() => {
    window.removeEventListener('online', updateOnlineStatus)
    window.removeEventListener('offline', updateOnlineStatus)
    if (unsubscribe) unsubscribe()
  })

  return {
    isOnline,
    isSyncing,
    pendingActionsCount,
    hasOfflineChanges,
    lastSyncResult,
    triggerSync,
    clearOfflineData,
    updatePendingCount
  }
}
